/**
 * hostValidation — Story 2.2 (AC-2): 422-Responses in Feld-Fehler uebersetzen.
 *
 * Kontrakt (openapi.yaml, ErrorBody): { error: { code, message, details? } }.
 * `details` ist ein freies Objekt — das Backend liefert dort Rails-uebliche
 * Feld-Fehler ({ feld: ["Nachricht", …] } oder { feld: "Nachricht" }).
 * Der Parser ist defensiv: unbekannte Formen fallen auf die globale
 * `message` zurueck, es wird nie eine Fehlermeldung erfunden.
 */
import { ApiError } from '../../lib/errors/ApiError'

/** Formularfelder des Host-Formulars (Story 2.1 Contract). */
export const HOST_FORM_FIELDS = [
  'name',
  'email',
  'phone',
  'solana_wallet_address',
  'notes',
  'currency_preference',
] as const

export type HostFormField = (typeof HOST_FORM_FIELDS)[number]

export type HostValidationErrors = {
  /** Feldbezogene Meldungen aus error.details. */
  fields: Partial<Record<HostFormField, string>>
  /** Globale Meldung (error.message) — immer gesetzt wenn 422 erkannt. */
  message: string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function firstMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) return value
  if (Array.isArray(value)) {
    const hit = value.find((v) => typeof v === 'string' && v.trim().length > 0)
    return (hit as string | undefined) ?? null
  }
  return null
}

/** Liefert den ErrorBody, egal ob als ApiError oder als roher Response-Body. */
function errorBodyOf(error: unknown): Record<string, unknown> | null {
  const body = error instanceof ApiError ? error.body : error
  if (isRecord(body) && isRecord(body.error)) return body.error
  return null
}

/**
 * Extrahiert Validierungs-Feedback aus einem geworfenen Fehler.
 * Gibt null zurueck, wenn es kein 422-Validierungsfehler ist —
 * der Aufrufer zeigt dann die generische Fehlermeldung.
 */
export function parseHostValidationError(error: unknown): HostValidationErrors | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null

  const result: HostValidationErrors = { fields: {}, message: null }

  const body = errorBodyOf(error)
  if (body) {
    if (typeof body.message === 'string' && body.message.trim().length > 0) {
      result.message = body.message
    }
    if (isRecord(body.details)) {
      for (const field of HOST_FORM_FIELDS) {
        const msg = firstMessage(body.details[field])
        if (msg) result.fields[field] = msg
      }
    }
  }

  return result
}

/**
 * Meldung aus einem beliebigen Fehler-Objekt — auch aus einem rohen
 * v2-ErrorBody, wie ihn `result.error` liefert, wenn die api-client-Middleware
 * die Antwort auf einem Randpfad nicht als ApiError geworfen hat.
 * null = keine verwertbare BE-Meldung, der Aufrufer nimmt seinen i18n-Text.
 */
export function hostErrorMessage(error: unknown): string | null {
  const body = errorBodyOf(error)
  if (!body) return null
  return typeof body.message === 'string' && body.message.trim().length > 0
    ? body.message
    : null
}
